import { HelpCircle, Trash2, ChevronDown } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Tooltip } from '../../../components/ui/Tooltip'
import { useScopeStore } from '../store'
import { getGuidance } from '../guidance'
import { getFieldById, getGroupById, getSectionById } from '../schema'
import { AML_VENDORS } from '../types'
import { ResellerLockBadge, useIsResellerLocked, useHasResellerParent, ResellerInheritedBanner } from './ResellerLock'
import { useUpsellContext } from '../hooks/useUpsellContext'

type TriState = 'yes' | 'no' | 'unknown'

function SectionHeader({
  title,
  subtitle,
  help,
}: {
  title: string
  subtitle?: string
  help?: string
}) {
  return (
    <div className="pt-2">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-base font-semibold text-[var(--text-primary)]">{title}</h4>
          {subtitle ? <p className="text-sm text-[var(--text-secondary)] mt-0.5">{subtitle}</p> : null}
        </div>
        {help ? (
          <Tooltip content={help}>
            <span className="ml-2 inline-flex align-middle">
              <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)] mt-1" />
            </span>
          </Tooltip>
        ) : null}
      </div>
    </div>
  )
}

function TriStateRadio({
  name,
  value,
  onChange,
  disabled,
}: {
  name: string
  value: TriState
  onChange: (v: TriState) => void
  disabled: boolean
}) {
  const { mapLabel } = useUpsellContext()
  const options: { value: TriState; label: string }[] = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: 'unknown', label: mapLabel("Don't know") },
  ]

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <label
          key={opt.value}
          className={`px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${
            value === opt.value
              ? opt.value === 'unknown'
                ? 'border-amber-500 bg-amber-500/15 text-amber-400 font-medium'
                : 'border-primary bg-primary/10 text-[var(--text-primary)]'
              : 'border-[var(--border-color)] text-[var(--text-secondary)] hover:border-gray-600'
          } ${disabled ? (value === opt.value ? 'cursor-not-allowed' : 'opacity-40 cursor-not-allowed') : ''}`}
        >
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            disabled={disabled}
            className="hidden"
          />
          {opt.value === 'unknown' && value === 'unknown' ? `⚠ ${opt.label}` : opt.label}
        </label>
      ))}
    </div>
  )
}

const PEP_LEVELS = [
  { id: '1', label: 'Level 1 - High Risk', description: 'Heads of state, government members, parliament members' },
  { id: '2', label: 'Level 2 - Medium Risk', description: 'Senior officials of military, judiciary, ambassadors' },
  { id: '3', label: 'Level 3 - Medium Risk', description: 'Senior management of state owned businesses' },
  { id: '4', label: 'Level 4 - Lower Risk', description: 'Mayors, local officials, PEP associates' },
]


// ComplyAdvantage Sanctions Sources - ALL sources from the complete list
const COMPLY_ADVANTAGE_SANCTIONS = [
  { id: '47', name: 'Australian National Security Terrorism List', country: 'AU', entities: 30 },
  { id: '72', name: 'Canada OSFI Entity List', country: 'CA', entities: 258 },
  { id: '73', name: 'Canada OSFI Person List', country: 'CA', entities: 575 },
  { id: '100', name: 'DFAT Australia Consolidated Sanctions List', country: 'AU', entities: 1942 },
  { id: '106', name: 'EU External Action Service - Consolidated list of Sanctions', country: 'XX', entities: 3047 },
  { id: '118', name: 'United States Financial Crimes Enforcement Network 311 Special Measures', country: 'US', entities: 23 },
  { id: '128', name: 'United Kingdom HM Treasury Office of Financial Sanctions Implementation Consolidated List', country: 'GB', entities: 3134 },
  { id: '129', name: 'HM Treasury Ukraine Sanctions', country: 'GB', entities: 11 },
  { id: '137', name: 'India Ministry of Home Affairs Banned Organisations', country: 'IN', entities: 43 },
  { id: '150', name: 'Israel National Bureau for Counter Terror Financing Designated individuals', country: 'IL', entities: 460 },
  { id: '160', name: 'Monaco Ministerial Orders', country: 'MC', entities: 18 },
  { id: '166', name: 'Sanction Netherlands Domestic Terrorist List', country: 'NL', entities: 166 },
  { id: '168', name: 'New Zealand Police Designated Terrorists', country: 'NZ', entities: 778 },
  { id: '174', name: 'Norway Lovdata laws judicial database', country: 'NO', entities: 445 },
  { id: '176', name: 'OFAC Consolidated List', country: 'US', entities: 648 },
  { id: '177', name: 'OFAC SDN List', country: 'US', entities: 10145 },
  { id: '192', name: 'Public Safety Canada Listed Entities', country: 'CA', entities: 85 },
  { id: '194', name: 'United States Department of State Counter Terrorism Rewards Program', country: 'US', entities: 115 },
  { id: '195', name: 'Russian ROSFIN Monitoring List Extremism and Terrorism', country: 'RU', entities: 13137 },
  { id: '200', name: 'Ukraine State Financial Monitoring Black List', country: 'UA', entities: 1339 },
  { id: '201', name: 'Singapore MAS List', country: 'SG', entities: 127 },
  { id: '205', name: 'DFATD Canada Special Economic Measures Act Designations', country: 'CA', entities: 1426 },
  { id: '208', name: 'Switzerland SECO List', country: 'CH', entities: 3090 },
  { id: '211', name: 'Thailand Designated Persons Sanctions List', country: 'TH', entities: 196 },
  { id: '212', name: 'Thailand Designated Persons List', country: 'TH', entities: 0 },
  { id: '216', name: 'France Tresor Direction Generale Liste Unique de Gels', country: 'FR', entities: 2552 },
  { id: '217', name: 'Turkey Ministry of Interior Wanted Terrorists', country: 'TR', entities: 1296 },
  { id: '223', name: 'United Nations Consolidated', country: 'XX', entities: 1291 },
  { id: '224', name: 'UN Liberia Sanctions', country: 'XX', entities: 51 },
  { id: '232', name: 'United States Bureau of Industry and Security The Denied Persons List', country: 'US', entities: 548 },
  { id: '234', name: 'United States Department of State Terror Exclusion List', country: 'US', entities: 59 },
  { id: '274', name: 'Arab states Sanctions List against Qatar related individuals', country: 'XX', entities: 59 },
  { id: '281', name: 'Sanction Related Entities', country: 'XX', entities: 1873 },
  { id: '295', name: 'United States Consolidated Screening List (CSL)', country: 'US', entities: 0 },
  { id: '296', name: 'Canadian Freezing Assets of Corrupt Foreign Officials Act', country: 'CA', entities: 26 },
  { id: '297', name: 'Belgium Federal Public Service Finance National Financial Sanctions', country: 'BE', entities: 300 },
  { id: '307', name: 'Singapore First Schedule of the Terrorism (Suppression of Financing) Act', country: 'SG', entities: 45 },
  { id: '311', name: 'United States Bureau of Industry and Security Entity List', country: 'US', entities: 1523 },
  { id: '312', name: 'United States Bureau of Industry and Security Unverified List', country: 'US', entities: 235 },
  { id: '313', name: 'United States Department of State Nonproliferation Sanctions List', country: 'US', entities: 426 },
  { id: '314', name: 'United States Department of State Cuban Restricted Entities List', country: 'US', entities: 237 },
  { id: '315', name: 'Canada Sanctions Justice for Victims of Corrupt Foreign Officials Act', country: 'CA', entities: 72 },
  { id: '316', name: 'Switzerland Federal Council Freezing of Foreign Illicit Assets', country: 'CH', entities: 79 },
  { id: '320', name: 'China Ministry of Public Security Terrorist list', country: 'CN', entities: 29 },
  { id: '322', name: 'European Council Restrictive Measures on Russian entities destabilising the situation in Ukraine', country: 'XX', entities: 20 },
  { id: '335', name: 'Philippines AMLC Sanctions Resolutions', country: 'PH', entities: 1705 },
  { id: '336', name: 'United Nations Security Council List of Designated Vessels', country: 'XX', entities: 59 },
  { id: '464', name: 'Australia Autonomous Sanctions for Russia Crimea and Sevastopol', country: 'AU', entities: 11 },
  { id: '1562', name: 'Ministry of Finance Japan Economic Sanctions List', country: 'JP', entities: 1311 },
  { id: '2278', name: 'South Korea Ministry of Economy and Finance Transaction Ban Designations', country: 'KR', entities: 885 },
  { id: '2409', name: 'Japan METI Export Control Security Division End User list', country: 'JP', entities: 560 },
  { id: '2410', name: 'OFAC SDN List - Related Entities', country: 'US', entities: 0 },
  { id: '2594', name: 'Malaysia Ministry of Home Affairs List of Sanctioned Entities', country: 'MY', entities: 28 },
  { id: '2595', name: 'Malaysia Ministry of Home Affairs List of Sanctioned Individuals', country: 'MY', entities: 45 },
  { id: '2608', name: 'European Council List of Designated Vessels', country: 'XX', entities: 66 },
  { id: '2949', name: 'Austria Oesterreichische Nationalbank OENB Historical Sanctions', country: 'AT', entities: 10 },
  { id: '2950', name: 'Austria Oesterreichische Nationalbank OENB Sanctions', country: 'AT', entities: 48 },
  { id: '3031', name: 'United Kingdom Home Office List of Proscribed Terrorist Organisations', country: 'GB', entities: 103 },
  { id: '3465', name: 'Ireland Organisations Sanction Laws', country: 'IE', entities: 2 },
  { id: '3512', name: 'Singapore First Schedule of The Terrorism (Suppression of Financing Act) 2', country: 'SG', entities: 0 },
  { id: '3523', name: 'United Arab Emirates List of Designated Terrorist Organisations and Groups', country: 'AE', entities: 188 },
  { id: '3683', name: 'Qatar National Terrorist Designation Lists', country: 'QA', entities: 72 },
  { id: '3715', name: 'Vietnam Sanctions Ministry of Public Security Terrorism List', country: 'VN', entities: 62 },
  { id: '3883', name: 'Russian ROSFIN Monitoring WMD List', country: 'RU', entities: 226 },
  { id: '4009', name: 'Luxembourg Liste des Sanctions Financieres Internationales', country: 'LU', entities: 201 },
  { id: '4014', name: 'Iran March and May 2017 Sanctions against US entities', country: 'IR', entities: 26 },
  { id: '4015', name: 'Latvia National Sanctions List', country: 'LV', entities: 3 },
  { id: '4142', name: 'Indonesia PPATK List of Funders of Proliferation of WMD Sanctions', country: 'ID', entities: 227 },
  { id: '4154', name: 'Bulgaria Domestic Sanctions under the Council of Ministers Decision 265 of 2003', country: 'BG', entities: 562 },
  { id: '4166', name: 'United States Coast Guard Prohibited Vessels', country: 'US', entities: 216 },
  { id: '4195', name: 'UN Consolidated - Related Entities', country: 'XX', entities: 0 },
  { id: '4229', name: 'Russian Sanctions Federal Security Service List of Terrorist Organisations', country: 'RU', entities: 32 },
  { id: '4315', name: 'Pakistan Federal Investigation Agency High Profile Terrorists', country: 'PK', entities: 832 },
  { id: '4316', name: 'Malta Financial Services Authority (MFSA) National Sanctions', country: 'MT', entities: 2069 },
  { id: '4346', name: 'Ukraine Sanctions National Security and Defense Council (NSDC) Special Economic Organisations', country: 'UA', entities: 1608 },
  { id: '4347', name: 'Ukraine Sanctions National Security and Defense Council (NSDC) Special Economic Persons', country: 'UA', entities: 3122 },
  { id: '4370', name: 'Spain Ministry of Interior Wanted Terrorists', country: 'ES', entities: 11 },
  { id: '4375', name: 'Serbia Government Decisions on Terrorism related Sanctions Designations', country: 'RS', entities: 7 },
  { id: '4649', name: 'Sri Lanka Ministry of Defence National Sanctions Designations under UN Regulations', country: 'LK', entities: 198 },
  { id: '4654', name: 'Kenya Gazette Notices of Sanctions Designations under the Prevention of Terrorism Act', country: 'KE', entities: 96 },
  { id: '4683', name: 'Pakistan Federal Investigation Agency Other High Profile Terrorists', country: 'PK', entities: 626 },
  { id: '4716', name: 'Indonesia PPATK List of Suspected Terrorists and Terrorist Organizations DTTOT Sanctions', country: 'ID', entities: 518 },
  { id: '4717', name: 'Sri Lanka Ministry of Defence Sanctions Designations under UN Regulation concerning proliferation of WMD', country: 'LK', entities: 128 },
  { id: '4986', name: 'Ukraine Ministry of Interior Wanted Terrorists', country: 'UA', entities: 1899 },
  { id: '5011', name: 'Australia Autonomous Sanctions North Korea Designated Vessels', country: 'AU', entities: 18 },
  { id: '5020', name: 'Arab states Sanctions List against Qatar related organisations', country: 'XX', entities: 12 },
  { id: '5052', name: 'Azerbaijan Financial Intelligence Unit National Sanctions List', country: 'AZ', entities: 198 },
  { id: '5053', name: 'Belarus State Security Agency List of Organizations and Individuals Involved in Terrorist Activities', country: 'BY', entities: 1022 },
  { id: '5054', name: 'Israel Division of Sanctions Headquarters Prevention of Distribution and Financing of Weapons of Mass Destruction', country: 'IL', entities: 229 },
  { id: '5055', name: 'Kazakhstan Committee on Legal Statistics List of Terrorist and Extremist Organizations', country: 'KZ', entities: 25 },
  { id: '5056', name: 'Kyrgyzstan State Financial Intelligence Service National List of Sanctions', country: 'KG', entities: 1108 },
  { id: '5057', name: 'Russia Government Special Economic Measures on Ukraine', country: 'RU', entities: 932 },
  { id: '5146', name: 'Belgium Consolidated List of the National and European Sanctions', country: 'BE', entities: 2589 },
  { id: '5147', name: 'Hong Kong Special Administrative Region Sanctions issued under the UN Sanctions Ordinance', country: 'HK', entities: 877 },
  { id: '5148', name: 'Israel National Bureau for Counter Terror Financing Designated associations and terrorists organizations', country: 'IL', entities: 373 },
  { id: '5168', name: 'Czech Republic Sanctions Government Order to implement specific international measures', country: 'CZ', entities: 48 },
  { id: '5169', name: 'Pakistan National Counter Terrorism Authority List of Organizations Proscribed by the Ministry of Interior', country: 'PK', entities: 93 },
  { id: '5200', name: 'Tunisia Sanctions National list of persons, organizations and entities associated with terrorist offenses', country: 'TN', entities: 126 },
  { id: '5291', name: 'Argentina Ministerio de Relaciones Exteriores y Culto Sanciones de la ONU', country: 'AR', entities: 1018 },
  { id: '5292', name: 'Romania Terrorism Related Sanctions List', country: 'RO', entities: 252 },
  { id: '5456', name: 'Bangladesh Domestic Sanctions List', country: 'BD', entities: 8 },
  { id: '5457', name: 'Canada Regulations Implementing the United Nations Resolutions on the Suppression of Terrorism', country: 'CA', entities: 35 },
  { id: '5463', name: 'Norway Sanctions and Restrictive Measures', country: 'NO', entities: 25 },
  { id: '5592', name: 'Russia Central Bank Special Economic Measures Designations', country: 'RU', entities: 178 },
  { id: '5593', name: 'South Korea Financial Services Commission Transaction Ban Designations', country: 'KR', entities: 675 },
  { id: '5594', name: 'Turkey Financial Crimes Investigation Board Terrorism Asset Freezes', country: 'TR', entities: 525 },
  { id: '5828', name: 'Japan Public Security Intelligence Agency International Terrorist Organisations', country: 'JP', entities: 216 },
  { id: '5896', name: 'South Africa Targeted Financial Sanctions List organisations', country: 'ZA', entities: 234 },
  { id: '5897', name: 'South Africa Targeted Financial Sanctions List persons', country: 'ZA', entities: 317 },
  { id: '5945', name: 'Japan National Public Service Commission International Terrorists List', country: 'JP', entities: 524 },
  { id: '5946', name: 'Jersey Proscribed Terrorist Organizations', country: 'JE', entities: 101 },
  { id: '6173', name: 'Kyrgyzstan State Commission for Religious Affairs Prohibited Organisations', country: 'KG', entities: 21 },
  { id: '6291', name: 'Taiwan Ministry of Justice Investigation Bureau Sanctions under Anti-terrorism Law', country: 'TW', entities: 26 },
  { id: '6381', name: 'Kazakhstan Ministry of Finance Organizations and Individuals Associated with the Financing of Terrorism', country: 'KZ', entities: 1927 },
  { id: '6449', name: 'Finland National Bureau of Investigation Asset Freeze List', country: 'FI', entities: 58 },
  { id: '6642', name: 'Bahrain Terrorists List', country: 'BH', entities: 204 },
  { id: '6644', name: 'Mexico Financial Intelligence Unit UN Sanctions for terrorism and non-proliferation', country: 'MX', entities: 714 },
  { id: '6739', name: 'United Arab Emirates National List of Terrorist Individuals and Entities in Arabic', country: 'AE', entities: 219 },
  { id: '6925', name: 'South Korea Office for Government Policy Coordination North Korea Sanctions List', country: 'KR', entities: 141 },
  { id: '7144', name: 'Republic of Moldova National Terrorist List', country: 'MD', entities: 527 },
  { id: '7146', name: 'Pakistan National Counter Terrorism Authority Schedule IV Proscribed Persons', country: 'PK', entities: 3800 },
  { id: '7313', name: 'United Kingdom Sanctions Anti-Money Laundering Act 2018', country: 'GB', entities: 81 },
  { id: '7465', name: 'Lithuania Designated Persons Under Magnitsky Amendments', country: 'LT', entities: 225 },
  { id: '7473', name: 'Estonia Designated Persons under Magnitsky Amendments', country: 'EE', entities: 205 },
  { id: '7474', name: 'Latvia Designated Persons under Magnitsky Amendments', country: 'LV', entities: 49 },
  { id: '7650', name: 'Monaco Economic Sanctions', country: 'MC', entities: 4165 },
  { id: '7651', name: 'Philippines Bangko Sentral Philippinas Circulars on Sanctioned Entities after 2012', country: 'PH', entities: 79 },
  { id: '7653', name: 'Argentina Public Registry of People and Entities linked to acts of Terrorism and Terrorism Financing', country: 'AR', entities: 517 },
  { id: '7654', name: 'Germany Prohibited Organizations', country: 'DE', entities: 88 },
  { id: '7764', name: 'Thailand Designated Persons Thai Version', country: 'TH', entities: 198 },
  { id: '8998', name: 'Qatar National Terrorist Designation Lists Arabic Version', country: 'QA', entities: 72 },
  { id: '9341', name: 'India Ministry of Home Affairs Designated Terrorists Under Unlawful Activities (Prevention) Act', country: 'IN', entities: 30 },
  { id: '11036', name: 'Iceland Sanctions List', country: 'IS', entities: 2801 },
  { id: '11437', name: 'Israel National Bureau for Counter Terror Financing Seizures of property of terrorist operatives', country: 'IL', entities: 89 },
  { id: '11654', name: 'OFAC Non-SDN Communist Chinese Military Companies List', country: 'US', entities: 35 },
  { id: '11655', name: 'Japan Ministry of Economy, Trade and Industry Administrative Sanctions', country: 'JP', entities: 4 },
  { id: '11707', name: 'Philippines Anti-Terrorism Council Designations', country: 'PH', entities: 17 },
  { id: '11830', name: 'United States Bureau of Industry and Security Military End User List', country: 'US', entities: 115 },
]

// ComplyAdvantage Adverse Media Categories
const COMPLY_ADVANTAGE_ADVERSE_MEDIA = [
  { id: '7744', name: 'Adverse Media V2 Narcotics AML CFT', category: 'Narcotics / AML CFT' },
  { id: '7745', name: 'Adverse Media V2 Other Minor', category: 'Other Minor' },
  { id: '7746', name: 'Adverse Media V2 Violence Non AML CFT', category: 'Violence Non-AML CFT' },
  { id: '7947', name: 'Adverse Media V2 Cybercrime', category: 'Cybercrime' },
  { id: '7948', name: 'Adverse Media V2 Financial AML CFT', category: 'Financial AML CFT' },
  { id: '7949', name: 'Adverse Media V2 Financial Difficulty', category: 'Financial Difficulty' },
  { id: '7950', name: 'Adverse Media V2 Fraud Linked', category: 'Fraud Linked' },
  { id: '7951', name: 'Adverse Media V2 General AML CFT', category: 'General AML CFT' },
  { id: '7952', name: 'Adverse Media V2 Other Financial', category: 'Other Financial' },
  { id: '7953', name: 'Adverse Media V2 Other Serious', category: 'Other Serious' },
  { id: '7954', name: 'Adverse Media V2 Property', category: 'Property' },
  { id: '7955', name: 'Adverse Media V2 Regulatory', category: 'Regulatory' },
  { id: '7956', name: 'Adverse Media V2 Terrorism', category: 'Terrorism' },
  { id: '7957', name: 'Adverse Media V2 Violence AML CFT', category: 'Violence AML CFT' },
]

// ComplyAdvantage Global Watchlists - ALL sources from the complete list
const COMPLY_ADVANTAGE_WATCHLISTS = [
  { id: '5', name: 'Asian Development Bank Sanctions', country: 'XX', entities: 1263, type: 'warning' },
  { id: '12', name: 'Australia Border Force Notices', country: 'AU', entities: 203, type: 'warning' },
  { id: '14', name: 'Australia ASIC Banned and Disqualified Persons', country: 'AU', entities: 2965, type: 'fitness-probity' },
  { id: '15', name: 'Australian Competition and Consumer Commission Public Warning Notices', country: 'AU', entities: 2, type: 'warning' },
  { id: '45', name: 'British Virgin Islands BVI FSC Enforcement Notices', country: 'VG', entities: 47, type: 'warning' },
  { id: '51', name: 'Cayman Islands CIMA Enforcement Actions', country: 'KY', entities: 52, type: 'warning' },
  { id: '71', name: 'Canada RCMP National Sex Offender Registry', country: 'CA', entities: 45913, type: 'warning' },
  { id: '75', name: 'Denmark FSA Warnings', country: 'DK', entities: 1301, type: 'warning' },
  { id: '94', name: 'European Bank for Reconstruction and Development Ineligible Entities', country: 'XX', entities: 622, type: 'warning' },
  { id: '99', name: 'World Bank Debarred Firm List', country: 'XX', entities: 3068, type: 'warning' },
  { id: '102', name: 'European Union Non-Cooperative Jurisdictions for Tax Purposes', country: 'XX', entities: 0, type: 'warning' },
  { id: '109', name: 'FBI Most Wanted', country: 'US', entities: 763, type: 'warning' },
  { id: '110', name: 'UK Financial Conduct Authority Final Notices', country: 'GB', entities: 3407, type: 'warning' },
  { id: '111', name: 'UK Financial Conduct Authority Warnings', country: 'GB', entities: 6277, type: 'warning' },
  { id: '117', name: 'US FinCEN 314(a) Notice', country: 'US', entities: 4936, type: 'warning' },
  { id: '119', name: 'US Medicaid Exclusions Lists OIG LEIE', country: 'US', entities: 76496, type: 'fitness-probity' },
  { id: '131', name: 'Hong Kong Companies Registry Disqualifications', country: 'HK', entities: 50, type: 'fitness-probity' },
  { id: '132', name: 'Hong Kong SFC Warnings List', country: 'HK', entities: 3582, type: 'warning' },
  { id: '133', name: 'Australian Financial Security Authority Bankruptcy Search', country: 'AU', entities: 39, type: 'fitness-probity' },
  { id: '134', name: 'International Association of Insurance Supervisors IAIS Alerts Fraudulent Insurers', country: 'XX', entities: 152, type: 'warning' },
  { id: '135', name: 'InterAmerican Development Bank Sanctions', country: 'XX', entities: 598, type: 'warning' },
  { id: '138', name: 'UK Serious Fraud Office Convictions', country: 'GB', entities: 219, type: 'warning' },
  { id: '139', name: 'UK Insolvency Service Disqualified Directors', country: 'GB', entities: 4751, type: 'fitness-probity' },
  { id: '140', name: 'Ireland Central Bank Enforcement Outcomes', country: 'IE', entities: 73, type: 'warning' },
  { id: '141', name: 'Ireland Central Bank Unauthorised Firms', country: 'IE', entities: 176, type: 'warning' },
  { id: '143', name: 'Jersey FSC Warnings', country: 'JE', entities: 122, type: 'warning' },
  { id: '144', name: 'Interpol Wanted', country: 'XX', entities: 12673, type: 'warning' },
  { id: '159', name: 'Malta FSA Unauthorised Firms', country: 'MT', entities: 193, type: 'warning' },
  { id: '162', name: 'UK NCA Most Wanted', country: 'GB', entities: 126, type: 'warning' },
  { id: '164', name: 'New Zealand Companies Office Disqualified Directors', country: 'NZ', entities: 113, type: 'fitness-probity' },
  { id: '165', name: 'New Zealand Insolvency Register', country: 'NZ', entities: 2057, type: 'fitness-probity' },
  { id: '167', name: 'New Zealand FMA Warnings', country: 'NZ', entities: 224, type: 'warning' },
  { id: '175', name: 'US DEA Orange Book List', country: 'US', entities: 2039, type: 'warning' },
  { id: '184', name: 'Singapore ACRA Disqualified Directors', country: 'SG', entities: 28, type: 'fitness-probity' },
  { id: '185', name: 'Australia ATO Penalties List', country: 'AU', entities: 164, type: 'warning' },
  { id: '186', name: 'Australia ATO Tax Avoidance and Evasion Promoters', country: 'AU', entities: 6, type: 'warning' },
  { id: '187', name: 'US Securities and Exchange Commission Litigation', country: 'US', entities: 7042, type: 'warning' },
  { id: '199', name: 'United States Secret Service Most Wanted', country: 'US', entities: 60, type: 'warning' },
  { id: '204', name: 'Switzerland FINMA Warnings', country: 'CH', entities: 1076, type: 'warning' },
  { id: '207', name: 'Sweden FSA Warnings', country: 'SE', entities: 1106, type: 'warning' },
  { id: '218', name: 'UK HMRC Fraud Tax Avoidance Schemes', country: 'GB', entities: 5, type: 'warning' },
  { id: '219', name: 'Canada OSC Permanent Prohibitions', country: 'CA', entities: 1086, type: 'warning' },
  { id: '220', name: 'UK Company House Disqualified Directors', country: 'GB', entities: 15741, type: 'fitness-probity' },
  { id: '233', name: 'US Department of Justice Press Releases', country: 'US', entities: 8892, type: 'warning' },
  { id: '237', name: 'US Federal Trade Commission Press Releases', country: 'US', entities: 2195, type: 'warning' },
  { id: '254', name: 'US Commodity Futures Trading Commission Press Releases', country: 'US', entities: 1368, type: 'warning' },
  { id: '271', name: 'Belgium FSMA Warnings', country: 'BE', entities: 4129, type: 'warning' },
  { id: '277', name: 'Australian Media Law Enforcement', country: 'AU', entities: 34839, type: 'warning' },
  { id: '280', name: 'New Zealand Police Most Wanted', country: 'NZ', entities: 54, type: 'warning' },
  { id: '282', name: 'Australia Financial Services Register', country: 'AU', entities: 0, type: 'warning' },
  { id: '284', name: 'US Federal Reserve Enforcement Actions', country: 'US', entities: 10224, type: 'warning' },
  { id: '285', name: 'France AMF Warnings', country: 'FR', entities: 2693, type: 'warning' },
  { id: '286', name: 'Portugal CMVM Alerts', country: 'PT', entities: 1002, type: 'warning' },
  { id: '287', name: 'Spain CNMV Warnings', country: 'ES', entities: 1811, type: 'warning' },
  { id: '288', name: 'Australia AFP Most Wanted', country: 'AU', entities: 74, type: 'warning' },
  { id: '291', name: 'US Secret Service Most Wanted Fugitives', country: 'US', entities: 8, type: 'warning' },
  { id: '308', name: 'Canada RCMP Most Wanted', country: 'CA', entities: 124, type: 'warning' },
  { id: '309', name: 'UK Government Disqualified Directors England and Wales', country: 'GB', entities: 0, type: 'fitness-probity' },
  { id: '317', name: 'US Internal Revenue Service Most Wanted', country: 'US', entities: 50, type: 'warning' },
  { id: '329', name: 'Singapore MAS Enforcement Actions and Investor Alerts', country: 'SG', entities: 1230, type: 'warning' },
  { id: '341', name: 'Australia AML-CTF Enforcement Actions', country: 'AU', entities: 5, type: 'warning' },
  { id: '344', name: 'Hong Kong Securities and Futures Commission Disciplinary Actions', country: 'HK', entities: 2009, type: 'warning' },
  { id: '356', name: 'UK Insolvency Service Court Orders', country: 'GB', entities: 1133, type: 'fitness-probity' },
  { id: '371', name: 'US Immigration and Customs Enforcement Most Wanted', country: 'US', entities: 3178, type: 'warning' },
  { id: '414', name: 'US Marshals Service Most Wanted', country: 'US', entities: 1041, type: 'warning' },
  { id: '441', name: 'Gibraltar FSC Warnings', country: 'GI', entities: 78, type: 'warning' },
  { id: '455', name: 'EU ESMA Unauthorised Firms Warnings', country: 'XX', entities: 1230, type: 'warning' },
  { id: '457', name: 'Japan Financial Services Agency Warnings', country: 'JP', entities: 1062, type: 'warning' },
  { id: '465', name: 'Malaysia BNM Illegal Schemes and Scam Alerts', country: 'MY', entities: 1050, type: 'warning' },
  { id: '469', name: 'National Futures Association Background Affiliation Status Information Center', country: 'US', entities: 29758, type: 'fitness-probity' },
  { id: '470', name: 'Financial Conduct Authority Mutual Recognition of Funds', country: 'GB', entities: 71879, type: 'warning' },
  { id: '473', name: 'Australian Prudential Regulation Authority Disqualified Persons', country: 'AU', entities: 11, type: 'fitness-probity' },
  { id: '479', name: 'South Africa FSCA Debarred Representatives', country: 'ZA', entities: 1234, type: 'fitness-probity' },
  { id: '481', name: 'Isle of Man FSA Enforcement Actions', country: 'IM', entities: 2, type: 'warning' },
  { id: '1033', name: 'US Bureau of Alcohol, Tobacco, Firearms and Explosives Most Wanted', country: 'US', entities: 83, type: 'warning' },
  { id: '1044', name: 'Australia Federal Court Proceedings', country: 'AU', entities: 7033, type: 'warning' },
  { id: '1276', name: 'Australia ASIC Media Releases', country: 'AU', entities: 11054, type: 'warning' },
  { id: '1277', name: 'Ireland Central Bank Prohibited Financial Service Providers', country: 'IE', entities: 2215, type: 'warning' },
  { id: '1549', name: 'Hong Kong Insolvency Register', country: 'HK', entities: 18344, type: 'fitness-probity' },
  { id: '1763', name: 'Italy CONSOB Warnings', country: 'IT', entities: 5426, type: 'warning' },
  { id: '2221', name: 'International Organisation of Securities Commissions (IOSCO) Investor Alert', country: 'XX', entities: 0, type: 'warning' },
  { id: '2285', name: 'France National Assembly Members', country: 'FR', entities: 577, type: 'pep' },
  { id: '2315', name: 'South Africa FSCA Warnings', country: 'ZA', entities: 1308, type: 'warning' },
  { id: '2356', name: 'UK Parliament Members', country: 'GB', entities: 1473, type: 'pep' },
  { id: '2374', name: 'Cyprus Securities and Exchange Commission Withdrawals Warnings', country: 'CY', entities: 1089, type: 'warning' },
  { id: '2410', name: 'OFAC SDN List - Related Entities', country: 'US', entities: 0, type: 'warning' },
  { id: '2416', name: 'US Financial Industry Regulatory Authority Broker Dealer', country: 'US', entities: 3445, type: 'fitness-probity' },
  { id: '2437', name: 'Poland Komisja Nadzoru Finansowego Warnings', country: 'PL', entities: 3277, type: 'warning' },
  { id: '2453', name: 'Austria Financial Market Authority Warnings', country: 'AT', entities: 5349, type: 'warning' },
  { id: '2471', name: 'Canada AMF Quebec Warnings', country: 'CA', entities: 743, type: 'warning' },
  { id: '2496', name: 'Germany BaFin Warnings', country: 'DE', entities: 7029, type: 'warning' },
  { id: '2541', name: 'Indonesia OJK Warnings', country: 'ID', entities: 3473, type: 'warning' },
  { id: '2558', name: 'Luxembourg CSSF Warnings', country: 'LU', entities: 1569, type: 'warning' },
  { id: '2612', name: 'Israel Securities Authority Investor Alerts', country: 'IL', entities: 0, type: 'warning' },
  { id: '2613', name: 'South Korea Financial Services Commission Warnings', country: 'KR', entities: 9438, type: 'warning' },
  { id: '2670', name: 'Hong Kong Companies Registry Companies Winding Up Order', country: 'HK', entities: 3047, type: 'fitness-probity' },
  { id: '2745', name: 'Australia Criminal Offences Register Federal Court', country: 'AU', entities: 3668, type: 'warning' },
  { id: '2905', name: 'Canada Media Financial Crime', country: 'CA', entities: 84549, type: 'warning' },
  { id: '2980', name: 'UK Insolvency Individual Insolvency Register', country: 'GB', entities: 2086260, type: 'fitness-probity' },
  { id: '3012', name: 'Guernsey Financial Services Commission Warnings', country: 'GG', entities: 77, type: 'warning' },
  { id: '3148', name: 'United States Postal Inspection Service Most Wanted', country: 'US', entities: 52, type: 'warning' },
  { id: '3186', name: 'Financial Industry Regulatory Authority Arbitration Awards', country: 'US', entities: 12823, type: 'warning' },
  { id: '3196', name: 'Hong Kong Official Receiver Insolvency Registers', country: 'HK', entities: 0, type: 'fitness-probity' },
  { id: '3204', name: 'US Environmental Protection Agency EPA Press Releases', country: 'US', entities: 4063, type: 'warning' },
  { id: '3251', name: 'US National Sex Offenders Public Website', country: 'US', entities: 0, type: 'warning' },
  { id: '3263', name: 'New Zealand Financial Markets Authority Warnings', country: 'NZ', entities: 0, type: 'warning' },
  { id: '3313', name: 'Thailand Securities and Exchange Commission Warnings', country: 'TH', entities: 4, type: 'warning' },
  { id: '3356', name: 'Netherlands AFM Unauthorised Firms and Warnings', country: 'NL', entities: 3234, type: 'warning' },
  { id: '3424', name: 'Australian Criminal Intelligence Commission AUSTRAC SMSF Auditor Disqualification Register', country: 'AU', entities: 25, type: 'fitness-probity' },
  { id: '3425', name: 'Taiwan Financial Supervisory Commission Investor Alerts', country: 'TW', entities: 1050, type: 'warning' },
  { id: '3474', name: 'Hong Kong Official Receiver Insolvency Bankruptcy Register', country: 'HK', entities: 11516, type: 'fitness-probity' },
  { id: '3475', name: 'South Korea National Police Most Wanted', country: 'KR', entities: 26, type: 'warning' },
  { id: '3492', name: 'Japan National Police Agency Wanted', country: 'JP', entities: 533, type: 'warning' },
  { id: '3496', name: 'Czech Republic Ministry of Finance Sanctions', country: 'CZ', entities: 2, type: 'warning' },
  { id: '3509', name: 'Hungary Central Bank of Hungary Alerts', country: 'HU', entities: 2193, type: 'warning' },
  { id: '3520', name: 'Latvia Financial and Capital Market Commission Warnings', country: 'LV', entities: 1191, type: 'warning' },
  { id: '3527', name: 'Romania Financial Supervisory Authority Warnings', country: 'RO', entities: 2088, type: 'warning' },
  { id: '3539', name: 'Philippines Securities and Exchange Commission Investor Alerts', country: 'PH', entities: 148, type: 'warning' },
  { id: '3541', name: 'Slovenia Securities Market Agency Warnings', country: 'SI', entities: 410, type: 'warning' },
  { id: '3545', name: 'Estonia Financial Supervision Authority Warnings', country: 'EE', entities: 1152, type: 'warning' },
  { id: '3561', name: 'Norway Financial Supervisory Authority Warnings', country: 'NO', entities: 1324, type: 'warning' },
  { id: '3562', name: 'Bulgaria Financial Supervision Commission Warnings', country: 'BG', entities: 1191, type: 'warning' },
  { id: '3563', name: 'Lithuania Bank of Lithuania Warnings', country: 'LT', entities: 931, type: 'warning' },
  { id: '3564', name: 'Croatia Financial Services Supervisory Agency Warnings', country: 'HR', entities: 450, type: 'warning' },
  { id: '3565', name: 'Greece Hellenic Capital Market Commission Warnings', country: 'GR', entities: 1152, type: 'warning' },
  { id: '4195', name: 'UN Consolidated - Related Entities', country: 'XX', entities: 0, type: 'warning' },
  { id: '4250', name: 'United Arab Emirates Securities and Commodities Authority Investor Protection Warnings', country: 'AE', entities: 314, type: 'warning' },
  { id: '4272', name: 'Canada OSC Administrative Proceedings', country: 'CA', entities: 3341, type: 'warning' },
  { id: '4388', name: 'Bermuda Monetary Authority Regulatory Actions', country: 'BM', entities: 23, type: 'warning' },
  { id: '4441', name: 'Isle of Man Financial Services Authority Unauthorised Register', country: 'IM', entities: 0, type: 'warning' },
  { id: '4541', name: 'Mauritius Financial Services Commission Alerts and Warnings', country: 'MU', entities: 325, type: 'warning' },
  { id: '4543', name: 'Ireland Charities Regulatory Authority Enforcement', country: 'IE', entities: 1, type: 'warning' },
  { id: '4587', name: 'UK Police Most Wanted', country: 'GB', entities: 198, type: 'warning' },
]

export function AmlConfigTab() {
  const { scope, updateAmlConfig, togglePepLevel } = useScopeStore()
  const { amlConfig } = scope
  const resellerImported = useHasResellerParent()
  const disabled = scope.finalized || resellerImported
  const levelLocked = useIsResellerLocked('amlConfig', 'level')
  const vendorLocked = useIsResellerLocked('amlConfig', 'vendor')
  const monitoringTypeLocked = useIsResellerLocked('amlConfig', 'monitoringType')
  const [vendorDetailsExpanded, setVendorDetailsExpanded] = useState(false)
  const [sanctionsSearch, setSanctionsSearch] = useState('')
  const [showSanctionsDropdown, setShowSanctionsDropdown] = useState(false)
  const [adverseMediaSearch, setAdverseMediaSearch] = useState('')
  const [showAdverseMediaDropdown, setShowAdverseMediaDropdown] = useState(false)
  const [watchlistSearch, setWatchlistSearch] = useState('')
  const [showWatchlistDropdown, setShowWatchlistDropdown] = useState(false)

  const sanctionsInputRef = useRef<HTMLInputElement>(null)
  const sanctionsDropdownRef = useRef<HTMLDivElement>(null)
  const adverseMediaInputRef = useRef<HTMLInputElement>(null)
  const adverseMediaDropdownRef = useRef<HTMLDivElement>(null)
  const watchlistInputRef = useRef<HTMLInputElement>(null)
  const watchlistDropdownRef = useRef<HTMLDivElement>(null)

  const [sanctionsDropdownPos, setSanctionsDropdownPos] = useState({ top: 0, left: 0, width: 0 })
  const [adverseMediaDropdownPos, setAdverseMediaDropdownPos] = useState({ top: 0, left: 0, width: 0 })
  const [watchlistDropdownPos, setWatchlistDropdownPos] = useState({ top: 0, left: 0, width: 0 })

  // Update dropdown positions when they open or window scrolls/resizes
  useEffect(() => {
    if (showSanctionsDropdown && sanctionsInputRef.current) {
      const updatePosition = (e?: Event) => {
        // Don't update position if scrolling inside the dropdown itself
        if (e && e.target && sanctionsDropdownRef.current?.contains(e.target as Node)) {
          return
        }
        const rect = sanctionsInputRef.current!.getBoundingClientRect()
        setSanctionsDropdownPos({
          top: rect.bottom,
          left: rect.left,
          width: rect.width
        })
      }
      updatePosition()
      window.addEventListener('scroll', updatePosition, true)
      window.addEventListener('resize', updatePosition)
      return () => {
        window.removeEventListener('scroll', updatePosition, true)
        window.removeEventListener('resize', updatePosition)
      }
    }
  }, [showSanctionsDropdown])

  useEffect(() => {
    if (showAdverseMediaDropdown && adverseMediaInputRef.current) {
      const updatePosition = (e?: Event) => {
        // Don't update position if scrolling inside the dropdown itself
        if (e && e.target && adverseMediaDropdownRef.current?.contains(e.target as Node)) {
          return
        }
        const rect = adverseMediaInputRef.current!.getBoundingClientRect()
        setAdverseMediaDropdownPos({
          top: rect.bottom,
          left: rect.left,
          width: rect.width
        })
      }
      updatePosition()
      window.addEventListener('scroll', updatePosition, true)
      window.addEventListener('resize', updatePosition)
      return () => {
        window.removeEventListener('scroll', updatePosition, true)
        window.removeEventListener('resize', updatePosition)
      }
    }
  }, [showAdverseMediaDropdown])

  useEffect(() => {
    if (showWatchlistDropdown && watchlistInputRef.current) {
      const updatePosition = (e?: Event) => {
        // Don't update position if scrolling inside the dropdown itself
        if (e && e.target && watchlistDropdownRef.current?.contains(e.target as Node)) {
          return
        }
        const rect = watchlistInputRef.current!.getBoundingClientRect()
        setWatchlistDropdownPos({
          top: rect.bottom,
          left: rect.left,
          width: rect.width
        })
      }
      updatePosition()
      window.addEventListener('scroll', updatePosition, true)
      window.addEventListener('resize', updatePosition)
      return () => {
        window.removeEventListener('scroll', updatePosition, true)
        window.removeEventListener('resize', updatePosition)
      }
    }
  }, [showWatchlistDropdown])

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sanctionsDropdownRef.current && !sanctionsDropdownRef.current.contains(event.target as Node) &&
          sanctionsInputRef.current && !sanctionsInputRef.current.contains(event.target as Node)) {
        setShowSanctionsDropdown(false)
      }
      if (adverseMediaDropdownRef.current && !adverseMediaDropdownRef.current.contains(event.target as Node) &&
          adverseMediaInputRef.current && !adverseMediaInputRef.current.contains(event.target as Node)) {
        setShowAdverseMediaDropdown(false)
      }
      if (watchlistDropdownRef.current && !watchlistDropdownRef.current.contains(event.target as Node) &&
          watchlistInputRef.current && !watchlistInputRef.current.contains(event.target as Node)) {
        setShowWatchlistDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const tabHeaderGroup = getGroupById('aml_tab_header')
  const coreSetupSection = getSectionById('aml_core_setup')
  const amlLevelGroup = getGroupById('aml_check_level_group')
  const amlLevelField = getFieldById('aml_level')
  const amlVendorGroup = getGroupById('aml_vendor_group')
  const amlVendorField = getFieldById('aml_vendor')
  const amlOtherVendorField = getFieldById('aml_other_vendor')

  const screeningSettingsSection = getSectionById('aml_screening_settings')
  const pepLevelsGroup = getGroupById('aml_pep_levels_group')
  const monitoringTypeGroup = getGroupById('aml_monitoring_type_group')
  const pepLevelsField = getFieldById('aml_pep_levels')
  const monitoringTypeField = getFieldById('aml_monitoring_type')

  const screeningEntityTypesSection = getSectionById('aml_screening_entity_types')
  const screeningEntityTypesGroup = getGroupById('aml_screening_entity_types_group')
  const screeningIndividualsField = getFieldById('aml_screening_entity_individuals')
  const screeningBusinessesField = getFieldById('aml_screening_entity_businesses')

  const pepScreeningSection = getSectionById('aml_pep_screening_configuration')
  const pepScreeningGroup = getGroupById('aml_pep_screening_configuration_group')
  const pepIncludeFamilyField = getFieldById('aml_pep_include_family_members')
  const pepIncludeHistoricalField = getFieldById('aml_pep_include_historical_peps')

  const sanctionsSection = getSectionById('aml_sanctions_screening_configuration')
  const sanctionsGroup = getGroupById('aml_sanctions_screening_group')
  const sanctionsMatchActionField = getFieldById('aml_sanctions_match_action')

  const adverseMediaSection = getSectionById('aml_adverse_media_configuration')
  const adverseMediaGroup = getGroupById('aml_adverse_media_group')
  const adverseMediaCategoryField = getFieldById('aml_adverse_media_category')
  const adverseMediaMatchActionField = getFieldById('aml_adverse_media_match_action')

  const additionalWatchlistsSection = getSectionById('aml_additional_watchlists')
  const additionalWatchlistsGroup = getGroupById('aml_additional_watchlists_group')
  const additionalWatchlistsTypesField = getFieldById('aml_additional_watchlists_types')

  const amlConfigured = amlConfig.level !== ''
  const enhancedEnabled = amlConfig.level === 'enhanced'

  // Incomplete field highlighting — suppressed for upsells
  const { incompleteClass, isUpsell, mapLabel } = useUpsellContext()
  const isAmlLevelIncomplete = !isUpsell && (!amlConfig.level || amlConfig.level === 'unknown')
  const isAmlEnabled = amlConfig.level !== '' && amlConfig.level !== 'na' && amlConfig.level !== 'unknown'
  const isPepLevelsIncomplete = !isUpsell && isAmlEnabled && (amlConfig.pepLevels || []).length === 0
  const isVendorIncomplete = !isUpsell && isAmlEnabled && !amlConfig.vendor

  const toggleEnumInArray = <T extends string>(arr: T[], value: T): T[] => {
    return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]
  }

  const CheckboxMultiSelect = <T extends string>({
    options,
    value,
    onChange,
    disabled,
  }: {
    options: Array<{ id: T; label: string; help?: string }>
    value: T[]
    onChange: (next: T[]) => void
    disabled: boolean
  }) => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {options.map((opt) => (
          <label key={opt.id} className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 w-4 h-4 rounded border-[var(--border-color)] bg-[var(--bg-secondary)] text-primary focus:ring-primary"
              checked={value.includes(opt.id)}
              onChange={() => onChange(toggleEnumInArray(value, opt.id))}
              disabled={disabled}
            />
            <div>
              <div className="text-sm text-[var(--text-primary)] flex items-center gap-2">
                <span>{opt.label}</span>
                {opt.help ? (
                  <Tooltip content={opt.help}>
                    <span className="ml-2 inline-flex align-middle">
                      <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                    </span>
                  </Tooltip>
                ) : null}
              </div>
            </div>
          </label>
        ))}
      </div>
    )
  }

  const Section = ({
    title,
    subtitle,
    help,
    children,
  }: {
    title: string
    subtitle?: string
    help?: string
    children: React.ReactNode
  }) => {
    return (
      <div className="space-y-3">
        <SectionHeader title={title} subtitle={subtitle} help={help} />
        <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
          {children}
        </div>
      </div>
    )
  }

  const DisabledNotice = ({ children }: { children: string }) => {
    return (
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-300">
        {children}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <ResellerInheritedBanner />
      {/* Section Header */}
      <div className="border-b border-[var(--border-color)] pb-4">
        <h3 className="text-lg font-medium text-[var(--text-primary)]">
          {tabHeaderGroup?.formTitle || 'AML Configuration'}
        </h3>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          {tabHeaderGroup?.formDescription ||
            'Anti-Money Laundering screening and monitoring settings'}
        </p>
      </div>

      <SectionHeader
        title={coreSetupSection?.formTitle || 'Core setup'}
        subtitle={coreSetupSection?.formDescription || 'High-level AML screening decisions'}
      />

      <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
      {/* AML Check Level */}
      <div className={isAmlLevelIncomplete ? incompleteClass : ''}>
      <div className="border border-[var(--border-color)] rounded-lg p-4">
        <h4 className="text-sm font-medium text-[var(--text-primary)] mb-1">
          {amlLevelField?.formLabel || amlLevelGroup?.formTitle || 'AML Check Level'}
          <ResellerLockBadge section="amlConfig" field="level" />
          <Tooltip
            content={
              getGuidance('AML', 'AML check level') ||
              amlLevelField?.tooltip ||
              amlLevelGroup?.tooltip ||
              ''
            }
          >
            <span className="ml-2 inline-flex align-middle">
              <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
            </span>
          </Tooltip>
        </h4>
        <p className="text-xs text-[var(--text-muted)] mt-1">
          {amlLevelField?.formDescription ||
            amlLevelGroup?.formDescription ||
            'Choose the overall AML screening depth required.'}
        </p>
        <div className="space-y-3">
          <label className={`flex items-start gap-3 cursor-pointer p-3 rounded-lg transition-colors ${amlConfig.level === 'standard' ? 'bg-primary/10 border border-primary' : 'hover:bg-[var(--bg-secondary)]'}`}>
            <input
              type="radio"
              name="amlLevel"
              className="mt-1 w-4 h-4 text-primary focus:ring-primary"
              checked={amlConfig.level === 'standard'}
              onChange={() => updateAmlConfig('level', 'standard')}
              disabled={disabled || levelLocked}
            />
            <div>
              <span className="text-sm font-medium text-[var(--text-primary)]">
                {amlLevelField?.options?.find((o) => o.value === 'standard')?.label ||
                  'Standard AML Checks'}
              </span>
              <p className="text-xs text-[var(--text-muted)]">PEPs and Sanctions screening</p>
            </div>
          </label>
          <label className={`flex items-start gap-3 cursor-pointer p-3 rounded-lg transition-colors ${amlConfig.level === 'enhanced' ? 'bg-primary/10 border border-primary' : 'hover:bg-[var(--bg-secondary)]'}`}>
            <input
              type="radio"
              name="amlLevel"
              className="mt-1 w-4 h-4 text-primary focus:ring-primary"
              checked={amlConfig.level === 'enhanced'}
              onChange={() => updateAmlConfig('level', 'enhanced')}
              disabled={disabled || levelLocked}
            />
            <div>
              <span className="text-sm font-medium text-[var(--text-primary)]">
                {amlLevelField?.options?.find((o) => o.value === 'enhanced')?.label ||
                  'Enhanced AML Checks'}
              </span>
              <p className="text-xs text-[var(--text-muted)]">Standard + Adverse Media & Global Watchlists</p>
            </div>
          </label>
          <label className={`flex items-start gap-3 cursor-pointer p-3 rounded-lg transition-colors ${amlConfig.level === 'na' ? 'bg-primary/10 border border-primary' : 'hover:bg-[var(--bg-secondary)]'}`}>
            <input
              type="radio"
              name="amlLevel"
              className="mt-1 w-4 h-4 text-primary focus:ring-primary"
              checked={amlConfig.level === 'na'}
              onChange={() => updateAmlConfig('level', 'na')}
              disabled={disabled || levelLocked}
            />
            <div>
              <span className="text-sm font-medium text-[var(--text-primary)]">
                {amlLevelField?.options?.find((o) => o.value === 'na')?.label || 'Not Applicable'}
              </span>
              <p className="text-xs text-[var(--text-muted)]">No AML checks required</p>
            </div>
          </label>
          <label className={`flex items-start gap-3 cursor-pointer p-3 rounded-lg transition-colors ${amlConfig.level === 'unknown' ? 'bg-amber-500/10 border border-amber-500/50' : 'hover:bg-[var(--bg-secondary)]'}`}>
            <input
              type="radio"
              name="amlLevel"
              className="mt-1 w-4 h-4 text-primary focus:ring-primary"
              checked={amlConfig.level === 'unknown'}
              onChange={() => updateAmlConfig('level', 'unknown')}
              disabled={disabled || levelLocked}
            />
            <div>
              <span className="text-sm font-medium text-[var(--text-primary)]">
                {mapLabel("Don't know")}
              </span>
              <p className="text-xs text-[var(--text-muted)]">AML requirements not yet determined</p>
            </div>
          </label>
        </div>
      </div>
      </div>
      </div>

      {/* Only show these sections if AML is enabled */}
      {amlConfig.level !== 'na' && amlConfig.level !== 'unknown' && (
        <>
          {/* AML Vendor Selection */}
          <div className={isVendorIncomplete ? incompleteClass : ''}>
          <div className="border border-[var(--border-color)] rounded-lg p-4">
            <h4 className="text-sm font-medium text-[var(--text-primary)] mb-1">
              {amlVendorGroup?.formTitle || 'AML Vendor'}
              <ResellerLockBadge section="amlConfig" field="vendor" />
            </h4>
            <p className="text-xs text-[var(--text-secondary)] mt-1 mb-3">
              FrankieOne delivers AML screening and adverse media capabilities through two vendors. Both provide strong PEP, sanctions, and adverse media coverage — they differ in depth, tooling, and commercial model.
            </p>

            {/* Vendor comparison */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              {/* Refinitiv card */}
              <div
                className={`border rounded-lg overflow-hidden transition-colors ${
                  amlConfig.vendor === 'refinitiv'
                    ? 'border-purple-400 bg-purple-500/10'
                    : 'border-[var(--border-color)] hover:border-purple-300'
                }`}
              >
                <div
                  className="p-3 cursor-pointer"
                  onClick={() => !disabled && !vendorLocked && updateAmlConfig('vendor', amlConfig.vendor === 'refinitiv' ? '' : 'refinitiv')}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-semibold text-[var(--text-primary)]">Refinitiv (World-Check)</span>
                    {amlConfig.vendor === 'refinitiv' && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-100 text-purple-700 border border-purple-200">Selected</span>
                    )}
                  </div>
                  <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
                    Premium, enterprise-grade dataset — deeper/specialised datasets (e.g. vessels, expanded sanctions context) and richer resolution tooling.
                  </p>
                </div>
                {vendorDetailsExpanded && (
                  <div className="px-3 pb-3 border-t border-[var(--border-color)] pt-2">
                    <ul className="text-[11px] text-[var(--text-muted)] space-y-1.5 list-disc list-inside">
                      <li>Full PEP and sanctions coverage from World-Check</li>
                      <li>Adverse risk signals via <strong>Special Interest Categories (SICs)</strong> — structured classification flags (e.g. Fraud, Bribery & Corruption, Sanctions-Related, Tax Violations) derived from media, regulatory, and enforcement sources</li>
                      <li>SICs are curated risk tags on entity records, not raw article feeds — reduces noise and enables rule-based filtering</li>
                      <li>FrankieOne does not have access to Refinitiv's paid media hits (e.g. AFR, WSJ, SMH)</li>
                    </ul>
                  </div>
                )}
              </div>

              {/* ComplyAdvantage card */}
              <div
                className={`border rounded-lg overflow-hidden transition-colors ${
                  amlConfig.vendor === 'comply'
                    ? 'border-emerald-400 bg-emerald-500/10'
                    : 'border-[var(--border-color)] hover:border-emerald-300'
                }`}
              >
                <div
                  className="p-3 cursor-pointer"
                  onClick={() => !disabled && !vendorLocked && updateAmlConfig('vendor', amlConfig.vendor === 'comply' ? '' : 'comply')}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-semibold text-[var(--text-primary)]">ComplyAdvantage</span>
                    {amlConfig.vendor === 'comply' && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">Selected</span>
                    )}
                  </div>
                  <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
                    Pragmatic, cost/feature balanced option used by most customers. Strong PEP/sanctions/adverse media coverage with an easier commercial and onboarding model.
                  </p>
                </div>
                {vendorDetailsExpanded && (
                  <div className="px-3 pb-3 border-t border-[var(--border-color)] pt-2 text-[11px] text-[var(--text-muted)] space-y-2">
                    <div>
                      <span className="font-medium text-[var(--text-secondary)]">Sanctions:</span>
                      <span> UN (UNSC), OFAC (US SDN + related), EU consolidated, UK OFSI/HM Treasury, AU DFAT, plus country-level and thematic lists (WMD, terrorism, cyber). Updated as frequently as every 15 minutes.</span>
                    </div>
                    <div>
                      <span className="font-medium text-[var(--text-secondary)]">PEPs:</span>
                      <span> Full multi-level PEP universe (International, National, Sub-national, Local, SOE/SIE). Includes close associates, relatives, and associated entities. Configurable by country and PEP level.</span>
                    </div>
                    <div>
                      <span className="font-medium text-[var(--text-secondary)]">Adverse media:</span>
                      <span> Entity-level hits with AI/ML relevance extraction across multilingual open/web sources. Excludes paywalled/paid newspaper content.</span>
                    </div>
                    <div>
                      <span className="font-medium text-[var(--text-secondary)]">Monitoring:</span>
                      <span> Delta monitoring (daily default) with webhook/email alerts for added/updated/removed matches. Typically bundled first year; charged annually thereafter.</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              className="flex items-center gap-1 text-[11px] text-primary hover:underline cursor-pointer select-none mb-1"
              onClick={() => setVendorDetailsExpanded(!vendorDetailsExpanded)}
            >
              <ChevronDown size={12} className={`transition-transform ${vendorDetailsExpanded ? 'rotate-180' : ''}`} />
              {vendorDetailsExpanded ? 'Hide coverage details' : 'Show coverage details'}
            </button>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[var(--text-secondary)] mb-2">
                  {amlVendorField?.formLabel || 'Primary Vendor'}
                </label>
                <select
                  className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                  value={amlConfig.vendor}
                  onChange={(e) => updateAmlConfig('vendor', e.target.value)}
                  disabled={disabled}
                >
                  <option value="">Select vendor</option>
                  <option value="unknown">{mapLabel("Don't know")}</option>
                  {(
                    amlVendorField?.options || AML_VENDORS.map((v) => ({ value: v.id, label: v.label }))
                  ).map((v) => (
                    <option key={v.value} value={v.value}>
                      {v.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-[var(--text-muted)] mt-1">
                  <span className="text-primary">*</span>{' '}
                  {amlVendorGroup?.helperText || 'Required for completion.'}
                </p>
              </div>
              {amlConfig.vendor === 'other' && (
                <div>
                  <label className="block text-sm text-[var(--text-secondary)] mb-2">
                    {amlOtherVendorField?.formLabel || 'Other Vendor Name'}
                  </label>
                  <input
                    type="text"
                    className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                    placeholder={amlOtherVendorField?.placeholder || 'Enter vendor name'}
                    value={amlConfig.otherVendor}
                    onChange={(e) => updateAmlConfig('otherVendor', e.target.value)}
                    disabled={disabled}
                  />
                </div>
              )}
            </div>
          </div>
          </div>

          <SectionHeader
            title={screeningSettingsSection?.formTitle || 'Screening settings'}
            subtitle={
              screeningSettingsSection?.formDescription ||
              'PEP/sanctions screening behavior and matching thresholds'
            }
          />

          <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
          {/* PEP Levels */}
          <div className={isPepLevelsIncomplete ? incompleteClass : ''}>
          <div className="border border-[var(--border-color)] rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="text-sm font-medium text-[var(--text-primary)]">
                {pepLevelsGroup?.formTitle || pepLevelsField?.formLabel || 'PEP levels'}
              </h4>
              <Tooltip
                content={
                  getGuidance('AML', 'PEP levels') ||
                  pepLevelsField?.tooltip ||
                  pepLevelsGroup?.tooltip ||
                  ''
                }
              >
                <span className="ml-2 inline-flex align-middle">
                  <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                </span>
              </Tooltip>
            </div>
            <p className="text-xs text-[var(--text-muted)] mt-1 mb-4">
              {pepLevelsGroup?.formDescription || pepLevelsField?.formDescription ||
                'Select which Politically Exposed Person levels to screen.'}
            </p>
            <div className="space-y-3">
              {PEP_LEVELS.map((level) => (
                <label key={level.id} className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1 w-4 h-4 rounded border-[var(--border-color)] bg-[var(--bg-secondary)] text-primary focus:ring-primary"
                    checked={(amlConfig.pepLevels || []).includes(level.id)}
                    onChange={() => togglePepLevel(level.id)}
                    disabled={disabled}
                  />
                  <div>
                    <div className="text-sm text-[var(--text-primary)]">{level.label}</div>
                    <div className="text-xs text-[var(--text-muted)]">{level.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          </div>

          {/* Monitoring Type */}
          <div className="border border-[var(--border-color)] rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="text-sm font-medium text-[var(--text-primary)]">
                {monitoringTypeGroup?.formTitle || monitoringTypeField?.formLabel || 'Monitoring type'}
                <ResellerLockBadge section="amlConfig" field="monitoringType" />
              </h4>
              <Tooltip
                content={
                  getGuidance('AML', 'Monitoring type') ||
                  monitoringTypeField?.tooltip ||
                  monitoringTypeGroup?.tooltip ||
                  ''
                }
              >
                <span className="ml-2 inline-flex align-middle">
                  <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                </span>
              </Tooltip>
            </div>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              {monitoringTypeGroup?.formDescription ||
                monitoringTypeField?.formDescription ||
                'Choose whether screening is a one-off check or ongoing monitoring (typically daily).'}
            </p>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="monitoring"
                  className="w-4 h-4 text-primary focus:ring-primary"
                  checked={amlConfig.monitoringType === 'one-time'}
                  onChange={() => updateAmlConfig('monitoringType', 'one-time')}
                  disabled={disabled || monitoringTypeLocked}
                />
                <span className="text-sm text-[var(--text-primary)]">One-time Check</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="monitoring"
                  className="w-4 h-4 text-primary focus:ring-primary"
                  checked={amlConfig.monitoringType === 'ongoing'}
                  onChange={() => updateAmlConfig('monitoringType', 'ongoing')}
                  disabled={disabled || monitoringTypeLocked}
                />
                <span className="text-sm text-[var(--text-primary)]">Ongoing Monitoring</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="monitoring"
                  className="w-4 h-4 text-primary focus:ring-primary"
                  checked={amlConfig.monitoringType === 'unknown'}
                  onChange={() => updateAmlConfig('monitoringType', 'unknown')}
                  disabled={disabled || monitoringTypeLocked}
                />
                <span className="text-sm text-[var(--text-primary)]">{mapLabel("Don't know")}</span>
              </label>
            </div>

            {/* Ongoing Monitoring Enrolment Mode - only shown when monitoring type is ongoing */}
            {amlConfig.monitoringType === 'ongoing' && (
              <div className="mt-4 pl-1">
                <label className="block text-sm font-medium text-[var(--text-primary)] mb-1">
                  Enrolment Mode
                </label>
                <p className="text-xs text-[var(--text-muted)] mb-2">
                  Is ongoing monitoring enabled automatically for all entities, or manually opted in per entity?
                </p>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="monitoringEnrolment"
                      className="w-4 h-4 text-primary focus:ring-primary"
                      checked={amlConfig.ongoingMonitoringEnrolment === 'automatic'}
                      onChange={() => updateAmlConfig('ongoingMonitoringEnrolment', 'automatic')}
                      disabled={disabled}
                    />
                    <span className="text-sm text-[var(--text-primary)]">Automatic (all entities)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="monitoringEnrolment"
                      className="w-4 h-4 text-primary focus:ring-primary"
                      checked={amlConfig.ongoingMonitoringEnrolment === 'manual_opt_in'}
                      onChange={() => updateAmlConfig('ongoingMonitoringEnrolment', 'manual_opt_in')}
                      disabled={disabled}
                    />
                    <span className="text-sm text-[var(--text-primary)]">Manual Opt-in (per entity)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="monitoringEnrolment"
                      className="w-4 h-4 text-primary focus:ring-primary"
                      checked={amlConfig.ongoingMonitoringEnrolment === 'unknown'}
                      onChange={() => updateAmlConfig('ongoingMonitoringEnrolment', 'unknown')}
                      disabled={disabled}
                    />
                    <span className="text-sm text-[var(--text-primary)]">{mapLabel("Don't know")}</span>
                  </label>
                </div>
              </div>
            )}
          </div>
          </div>

          <Section
            title={
              screeningEntityTypesSection?.formTitle ||
              screeningEntityTypesGroup?.formTitle ||
              'Screening Entity Types'
            }
            subtitle={
              screeningEntityTypesSection?.formDescription ||
              screeningEntityTypesGroup?.formDescription ||
              'What entity types does the customer need to screen?'
            }
            help={screeningEntityTypesGroup?.tooltip}
          >
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-4">
                <div className="text-sm text-[var(--text-primary)]">
                  {screeningIndividualsField?.formLabel || 'Individuals'}
                </div>
                <TriStateRadio
                  name="screeningEntityTypes.individuals"
                  value={amlConfig.screeningEntityTypes.individuals}
                  onChange={(next) =>
                    updateAmlConfig('screeningEntityTypes', { ...amlConfig.screeningEntityTypes, individuals: next })
                  }
                  disabled={disabled}
                />
              </div>
              <p className="text-xs text-[var(--text-muted)]">
                {screeningIndividualsField?.formDescription ||
                  'Natural persons (customers/beneficiaries).'}
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-4">
                <div className="text-sm text-[var(--text-primary)]">
                  {screeningBusinessesField?.formLabel || 'Businesses'}
                </div>
                <TriStateRadio
                  name="screeningEntityTypes.businesses"
                  value={amlConfig.screeningEntityTypes.businesses}
                  onChange={(next) =>
                    updateAmlConfig('screeningEntityTypes', { ...amlConfig.screeningEntityTypes, businesses: next })
                  }
                  disabled={disabled}
                />
              </div>
              <p className="text-xs text-[var(--text-muted)]">
                {screeningBusinessesField?.formDescription || 'Companies / legal entities.'}
              </p>
            </div>
          </Section>

          <Section
            title={
              pepScreeningSection?.formTitle ||
              pepScreeningGroup?.formTitle ||
              'PEP Screening Configuration'
            }
            subtitle={
              pepScreeningSection?.formDescription ||
              pepScreeningGroup?.formDescription ||
              'More detailed PEP configuration (beyond levels).'
            }
            help={pepScreeningGroup?.tooltip}
          >
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-4">
                <div className="text-sm text-[var(--text-primary)]">
                  {pepIncludeFamilyField?.formLabel || 'Include family members'}
                </div>
                <TriStateRadio
                  name="pepScreeningConfig.includeFamilyMembers"
                  value={amlConfig.pepScreeningConfig.includeFamilyMembers}
                  onChange={(next) =>
                    updateAmlConfig('pepScreeningConfig', { ...amlConfig.pepScreeningConfig, includeFamilyMembers: next })
                  }
                  disabled={disabled}
                />
              </div>
              <p className="text-xs text-[var(--text-muted)]">
                {pepIncludeFamilyField?.formDescription ||
                  'Include PEP family members where supported by the vendor.'}
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-4">
                <div className="text-sm text-[var(--text-primary)]">
                  {pepIncludeHistoricalField?.formLabel || 'Include historical PEPs'}
                </div>
                <TriStateRadio
                  name="pepScreeningConfig.includeHistoricalPeps"
                  value={amlConfig.pepScreeningConfig.includeHistoricalPeps}
                  onChange={(next) =>
                    updateAmlConfig('pepScreeningConfig', { ...amlConfig.pepScreeningConfig, includeHistoricalPeps: next })
                  }
                  disabled={disabled}
                />
              </div>
              <p className="text-xs text-[var(--text-muted)]">
                {pepIncludeHistoricalField?.formDescription ||
                  'Screen for people who were previously PEPs (where supported).'}
              </p>
            </div>
          </Section>

          <Section
            title={
              sanctionsSection?.formTitle ||
              sanctionsGroup?.formTitle ||
              'Sanctions Screening Configuration'
            }
            subtitle={
              sanctionsSection?.formDescription ||
              sanctionsGroup?.formDescription ||
              'Enable sanctions screening and choose high-level handling.'
            }
            help={sanctionsGroup?.tooltip}
          >
            {!amlConfigured && <DisabledNotice>Choose an AML Check Level above to configure sanctions handling.</DisabledNotice>}
            <div>
              <label className="block text-sm text-[var(--text-secondary)] mb-2">
                {sanctionsMatchActionField?.formLabel || 'Match Action'}
              </label>
              <select
                className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                value={amlConfig.sanctionsScreeningConfig.matchAction}
                onChange={(e) =>
                  updateAmlConfig('sanctionsScreeningConfig', {
                    ...amlConfig.sanctionsScreeningConfig,
                    matchAction: e.target.value as any,
                  })
                }
                disabled={disabled || !amlConfigured}
              >
                {(
                  (sanctionsMatchActionField?.options || [
                    { value: 'tbd', label: 'TBD' },
                    { value: 'automatic_fail', label: 'Automatic Fail' },
                    { value: 'manual_review_required', label: 'Manual Review Required' },
                    { value: 'alert_only', label: 'Alert Only' },
                  ]) as Array<{ value: string; label: string }>
                ).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-4">
              <label className="block text-sm text-[var(--text-secondary)] mb-2">
                Sanctions Lists to Include
              </label>
              <p className="text-xs text-[var(--text-muted)] mb-3">
                Select which sanctions lists should be included in screening.
                {amlConfig.vendor === 'refinitiv' && (
                  <span className="block mt-1">
                    <a
                      href="https://docs.google.com/spreadsheets/d/19im6wD9BnnNn27W6HY6RHLgOxB1J2qbf/edit?gid=2081824492#gid=2081824492"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      See Refinitiv sanctions list reference →
                    </a>
                  </span>
                )}
                {amlConfig.vendor === 'comply' && (
                  <span className="block mt-1">
                    <a
                      href="https://docs.google.com/spreadsheets/d/14DKT1DkzzPjDsbuMwkyOivPtRC7n686y/edit?gid=1197965231#gid=1197965231"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      See ComplyAdvantage sanctions list reference →
                    </a>
                  </span>
                )}
              </p>

              {/* ComplyAdvantage Specific Sanctions Selector */}
              {amlConfig.vendor === 'comply' && (
                <div className="mt-4 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg p-4">
                  <h5 className="text-sm font-medium text-[var(--text-primary)] mb-3">
                    Select Specific ComplyAdvantage Sanctions Lists
                  </h5>
                  <p className="text-xs text-[var(--text-muted)] mb-3">
                    Search and select specific sanctions lists from ComplyAdvantage. These are recommended lists marked as "Select: Yes" in the reference.
                  </p>

                  {/* Search Input */}
                  <div className="mb-3">
                    <input
                      ref={sanctionsInputRef}
                      type="text"
                      placeholder="Search sanctions lists by name or country..."
                      className="w-full h-10 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary"
                      value={sanctionsSearch}
                      onChange={(e) => {
                        setSanctionsSearch(e.target.value)
                        if (!showSanctionsDropdown) {
                          setShowSanctionsDropdown(true)
                        }
                      }}
                      onClick={() => {
                        if (!showSanctionsDropdown) {
                          setShowSanctionsDropdown(true)
                        }
                      }}
                      onFocus={() => {
                        if (!showSanctionsDropdown) {
                          setShowSanctionsDropdown(true)
                        }
                      }}
                      disabled={disabled}
                    />

                    {/* Dropdown Results */}
                    {showSanctionsDropdown && createPortal(
                      <div
                        ref={sanctionsDropdownRef}
                        className="fixed z-[9999] bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-lg"
                        style={{
                          top: `${sanctionsDropdownPos.top}px`,
                          left: `${sanctionsDropdownPos.left}px`,
                          width: `${sanctionsDropdownPos.width}px`
                        }}
                      >
                        <div className="max-h-60 overflow-y-auto">
                          {COMPLY_ADVANTAGE_SANCTIONS
                              .filter(list => {
                                const selected = amlConfig.sanctionsScreeningConfig.complyAdvanceSanctionIds || []
                                const searchLower = sanctionsSearch.toLowerCase()
                                const matchesSearch = !sanctionsSearch ||
                                  list.name.toLowerCase().includes(searchLower) ||
                                  list.country.toLowerCase().includes(searchLower)
                                return !selected.includes(list.id) && matchesSearch
                              })
                              .slice(0, 50) // Limit to 50 results for performance
                              .map(list => (
                                <button
                                  key={list.id}
                                  type="button"
                                  className="w-full px-4 py-3 text-left hover:bg-[var(--bg-tertiary)] border-b border-[var(--border-color)] last:border-b-0"
                                  onClick={() => {
                                    const current = amlConfig.sanctionsScreeningConfig.complyAdvanceSanctionIds || []
                                    updateAmlConfig('sanctionsScreeningConfig', {
                                      ...amlConfig.sanctionsScreeningConfig,
                                      complyAdvanceSanctionIds: [...current, list.id]
                                    })
                                    setSanctionsSearch('')
                                    setShowSanctionsDropdown(false)
                                  }}
                                >
                                  <div className="text-sm font-medium text-[var(--text-primary)]">
                                    {list.name}
                                    <span className="ml-2 text-xs px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded">
                                      {list.country}
                                    </span>
                                  </div>
                                  <div className="text-xs text-[var(--text-muted)] mt-0.5">
                                    {list.entities.toLocaleString()} entities
                                  </div>
                                </button>
                              ))}
                        </div>
                      </div>,
                      document.body
                    )}
                  </div>

                  {/* Selected Lists */}
                  {(amlConfig.sanctionsScreeningConfig.complyAdvanceSanctionIds || []).length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-[var(--text-secondary)] mb-2">
                        Selected Lists ({(amlConfig.sanctionsScreeningConfig.complyAdvanceSanctionIds || []).length})
                      </div>
                      <div className="space-y-2">
                        {(amlConfig.sanctionsScreeningConfig.complyAdvanceSanctionIds || []).map(id => {
                          const list = COMPLY_ADVANTAGE_SANCTIONS.find(l => l.id === id)
                          if (!list) return null
                          return (
                            <div
                              key={id}
                              className="flex items-center justify-between bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 py-2"
                            >
                              <div className="flex-1">
                                <div className="text-sm font-medium text-[var(--text-primary)]">
                                  {list.name}
                                  <span className="ml-2 text-xs px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded">
                                    {list.country}
                                  </span>
                                </div>
                                <div className="text-xs text-[var(--text-muted)]">
                                  {list.entities.toLocaleString()} entities
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const current = amlConfig.sanctionsScreeningConfig.complyAdvanceSanctionIds || []
                                  updateAmlConfig('sanctionsScreeningConfig', {
                                    ...amlConfig.sanctionsScreeningConfig,
                                    complyAdvanceSanctionIds: current.filter(i => i !== id)
                                  })
                                }}
                                disabled={disabled}
                                className="ml-2 p-1 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors disabled:opacity-50"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Only show generic checkboxes for non-ComplyAdvantage vendors */}
              {amlConfig.vendor !== 'comply' && (
                <div className="space-y-2">
                  {[
                    { id: 'all_major', label: 'All Major Lists' },
                    { id: 'ofac', label: 'OFAC (US)' },
                    { id: 'un', label: 'UN Security Council' },
                    { id: 'eu', label: 'EU Sanctions' },
                    { id: 'uk_hmt', label: 'UK HM Treasury' },
                    { id: 'dfat', label: 'DFAT (Australia)' },
                    { id: 'country_specific', label: 'Country-specific lists' },
                    { id: 'custom', label: 'Custom lists (specify below)' },
                  ].map((option) => (
                    <label key={option.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-[var(--border-color)] bg-[var(--bg-secondary)] text-primary focus:ring-primary"
                        checked={(amlConfig.sanctionsScreeningConfig.listsToInclude || []).includes(option.id as any)}
                        onChange={(e) => {
                          const current = amlConfig.sanctionsScreeningConfig.listsToInclude || []
                          const updated = e.target.checked
                            ? [...current, option.id]
                            : current.filter((id) => id !== option.id)
                          updateAmlConfig('sanctionsScreeningConfig', {
                            ...amlConfig.sanctionsScreeningConfig,
                            listsToInclude: updated as any,
                          })
                        }}
                        disabled={disabled || !amlConfigured}
                      />
                      <span className="text-sm text-[var(--text-primary)]">{option.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {(amlConfig.sanctionsScreeningConfig.listsToInclude || []).includes('custom' as any) && (
              <div className="mt-4">
                <label className="block text-sm text-[var(--text-secondary)] mb-2">
                  Custom Sanctions Lists
                </label>
                <textarea
                  className="w-full h-20 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 resize-none"
                  placeholder="e.g., Hong Kong HKMA, Canada OSFI, Custom client lists..."
                  value={amlConfig.sanctionsScreeningConfig.customLists || ''}
                  onChange={(e) =>
                    updateAmlConfig('sanctionsScreeningConfig', {
                      ...amlConfig.sanctionsScreeningConfig,
                      customLists: e.target.value,
                    })
                  }
                  disabled={disabled}
                />
              </div>
            )}
          </Section>

          <Section
            title={
              adverseMediaSection?.formTitle ||
              adverseMediaGroup?.formTitle ||
              'Adverse Media Configuration'
            }
            subtitle={
              adverseMediaSection?.formDescription ||
              adverseMediaGroup?.formDescription ||
              'Enable adverse media screening and choose source tier.'
            }
            help={adverseMediaGroup?.tooltip}
          >
            {!enhancedEnabled && (
              <DisabledNotice>
                Adverse media options are available only when AML Check Level is set to Enhanced.
              </DisabledNotice>
            )}

            <div>
              <label className="block text-sm text-[var(--text-secondary)] mb-2">
                Adverse Media Categories
              </label>
              <p className="text-xs text-[var(--text-muted)] mb-3">
                Select which adverse media categories to monitor.
                {amlConfig.vendor === 'refinitiv' && (
                  <span className="block mt-1">
                    <a
                      href="https://docs.google.com/spreadsheets/d/19im6wD9BnnNn27W6HY6RHLgOxB1J2qbf/edit?gid=2081824492#gid=2081824492"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      See Refinitiv media list reference →
                    </a>
                  </span>
                )}
                {amlConfig.vendor === 'comply' && (
                  <span className="block mt-1">
                    <a
                      href="https://docs.google.com/spreadsheets/d/14DKT1DkzzPjDsbuMwkyOivPtRC7n686y/edit?gid=1197965231#gid=1197965231"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      See ComplyAdvantage media list reference →
                    </a>
                  </span>
                )}
              </p>

              {/* ComplyAdvantage Adverse Media Selector */}
              {amlConfig.vendor === 'comply' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                    Select Adverse Media Categories
                  </label>

                  {/* Search Input */}
                  <div className="mb-3">
                    <input
                      ref={adverseMediaInputRef}
                      type="text"
                      className="w-full h-10 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                      placeholder="Search adverse media categories..."
                      value={adverseMediaSearch}
                      onChange={(e) => {
                        setAdverseMediaSearch(e.target.value)
                        if (!showAdverseMediaDropdown) {
                          setShowAdverseMediaDropdown(true)
                        }
                      }}
                      onClick={() => {
                        if (!showAdverseMediaDropdown) {
                          setShowAdverseMediaDropdown(true)
                        }
                      }}
                      onFocus={() => {
                        if (!showAdverseMediaDropdown) {
                          setShowAdverseMediaDropdown(true)
                        }
                      }}
                      disabled={disabled || !enhancedEnabled}
                    />

                    {/* Dropdown Results */}
                    {showAdverseMediaDropdown && !disabled && enhancedEnabled && createPortal(
                      <div
                        ref={adverseMediaDropdownRef}
                        className="fixed z-[9999] bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-lg"
                        style={{
                          top: `${adverseMediaDropdownPos.top}px`,
                          left: `${adverseMediaDropdownPos.left}px`,
                          width: `${adverseMediaDropdownPos.width}px`
                        }}
                      >
                        <div className="max-h-64 overflow-y-auto">
                          {COMPLY_ADVANTAGE_ADVERSE_MEDIA
                            .filter(media => {
                              const selected = amlConfig.adverseMediaScreeningConfig.complyAdvanceMediaIds || []
                              const searchLower = adverseMediaSearch.toLowerCase()
                              const matchesSearch = !adverseMediaSearch ||
                                media.name.toLowerCase().includes(searchLower) ||
                                media.category.toLowerCase().includes(searchLower)
                              return !selected.includes(media.id) && matchesSearch
                            })
                            .map(media => (
                              <button
                                key={media.id}
                                type="button"
                                onClick={() => {
                                  const current = amlConfig.adverseMediaScreeningConfig.complyAdvanceMediaIds || []
                                  updateAmlConfig('adverseMediaScreeningConfig', {
                                    ...amlConfig.adverseMediaScreeningConfig,
                                    complyAdvanceMediaIds: [...current, media.id]
                                  })
                                  setAdverseMediaSearch('')
                                  setShowAdverseMediaDropdown(false)
                                }}
                                className="w-full px-4 py-3 text-left hover:bg-[var(--bg-tertiary)] transition-colors border-b border-[var(--border-color)] last:border-b-0"
                              >
                                <div className="flex items-center gap-2">
                                  <div className="flex-1">
                                    <div className="text-sm font-medium text-[var(--text-primary)]">
                                      {media.name}
                                    </div>
                                    <div className="text-xs text-[var(--text-muted)] mt-0.5">
                                      {media.category}
                                    </div>
                                  </div>
                                </div>
                              </button>
                            ))}
                        </div>
                      </div>,
                      document.body
                    )}
                  </div>

                  {/* Selected Media Categories */}
                  {(amlConfig.adverseMediaScreeningConfig.complyAdvanceMediaIds || []).length > 0 && (
                    <div className="space-y-1.5">
                      <div className="text-xs text-[var(--text-muted)] mb-2">
                        Selected: {(amlConfig.adverseMediaScreeningConfig.complyAdvanceMediaIds || []).length} categories
                      </div>
                      {(amlConfig.adverseMediaScreeningConfig.complyAdvanceMediaIds || []).map(id => {
                        const media = COMPLY_ADVANTAGE_ADVERSE_MEDIA.find(m => m.id === id)
                        if (!media) return null

                        return (
                          <div
                            key={id}
                            className="flex items-center gap-2 px-3 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg"
                          >
                            <div className="flex-1">
                              <div className="text-sm font-medium text-[var(--text-primary)]">
                                {media.name}
                              </div>
                              <div className="text-xs text-[var(--text-muted)]">
                                {media.category}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const current = amlConfig.adverseMediaScreeningConfig.complyAdvanceMediaIds || []
                                updateAmlConfig('adverseMediaScreeningConfig', {
                                  ...amlConfig.adverseMediaScreeningConfig,
                                  complyAdvanceMediaIds: current.filter(i => i !== id)
                                })
                              }}
                              disabled={disabled}
                              className="ml-2 p-1 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors disabled:opacity-50"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Only show generic checkboxes for non-ComplyAdvantage vendors */}
              {amlConfig.vendor !== 'comply' && (
                <div className="space-y-2">
                  {[
                    { id: 'all', label: 'All Categories' },
                    { id: 'financial_crime', label: 'Financial Crime' },
                    { id: 'violent_crime', label: 'Violent Crime' },
                    { id: 'cybercrime', label: 'Cybercrime' },
                    { id: 'regulatory_enforcement', label: 'Regulatory Enforcement' },
                    { id: 'corruption', label: 'Corruption' },
                    { id: 'human_rights', label: 'Human Rights Violations' },
                    { id: 'environmental', label: 'Environmental Crimes' },
                  ].map((option) => (
                    <label key={option.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-[var(--border-color)] bg-[var(--bg-secondary)] text-primary focus:ring-primary"
                        checked={(amlConfig.adverseMediaScreeningConfig.categories || []).includes(option.id as any)}
                        onChange={(e) => {
                          const current = amlConfig.adverseMediaScreeningConfig.categories || []
                          const updated = e.target.checked
                            ? [...current, option.id]
                            : current.filter((id) => id !== option.id)
                          updateAmlConfig('adverseMediaScreeningConfig', {
                            ...amlConfig.adverseMediaScreeningConfig,
                            categories: updated as any,
                          })
                        }}
                        disabled={disabled || !enhancedEnabled}
                      />
                      <span className="text-sm text-[var(--text-primary)]">{option.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {(amlConfig.adverseMediaScreeningConfig.categories || []).length > 0 && (
              <div className="mt-4">
                <label className="block text-sm text-[var(--text-secondary)] mb-2">
                  Custom Categories
                </label>
                <textarea
                  className="w-full h-20 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 resize-none"
                  placeholder="e.g., Tax evasion, securities fraud, specific industry violations..."
                  value={amlConfig.adverseMediaScreeningConfig.customCategories || ''}
                  onChange={(e) =>
                    updateAmlConfig('adverseMediaScreeningConfig', {
                      ...amlConfig.adverseMediaScreeningConfig,
                      customCategories: e.target.value,
                    })
                  }
                  disabled={disabled}
                />
              </div>
            )}

            <div>
              <label className="block text-sm text-[var(--text-secondary)] mb-2">
                {adverseMediaCategoryField?.formLabel || 'Category'}
              </label>
              <select
                className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                value={amlConfig.adverseMediaScreeningConfig.category}
                onChange={(e) =>
                  updateAmlConfig('adverseMediaScreeningConfig', {
                    ...amlConfig.adverseMediaScreeningConfig,
                    category: e.target.value as any,
                  })
                }
                disabled={disabled || !enhancedEnabled}
              >
                {(
                  (adverseMediaCategoryField?.options || [
                    { value: 'tbd', label: 'TBD' },
                    { value: 'standard', label: 'Standard' },
                    { value: 'premium', label: 'Premium' },
                    { value: 'both', label: 'Both' },
                  ]) as Array<{ value: string; label: string }>
                ).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-[var(--text-secondary)] mb-2">
                {adverseMediaMatchActionField?.formLabel || 'Match Action'}
              </label>
              <select
                className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                value={amlConfig.adverseMediaScreeningConfig.matchAction}
                onChange={(e) =>
                  updateAmlConfig('adverseMediaScreeningConfig', {
                    ...amlConfig.adverseMediaScreeningConfig,
                    matchAction: e.target.value as any,
                  })
                }
                disabled={disabled || !enhancedEnabled}
              >
                {(
                  (adverseMediaMatchActionField?.options || [
                    { value: 'tbd', label: 'TBD' },
                    { value: 'manual_review_required', label: 'Manual Review Required' },
                    { value: 'alert_only', label: 'Alert Only' },
                    { value: 'risk_score_impact', label: 'Risk Score Impact' },
                  ]) as Array<{ value: string; label: string }>
                ).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </Section>

          <Section
            title={
              additionalWatchlistsSection?.formTitle ||
              additionalWatchlistsGroup?.formTitle ||
              'Additional Watchlists'
            }
            subtitle={
              additionalWatchlistsSection?.formDescription ||
              additionalWatchlistsGroup?.formDescription ||
              'Extra list types beyond standard sanctions/PEP/adverse media.'
            }
            help={additionalWatchlistsGroup?.tooltip}
          >
            {!enhancedEnabled && (
              <DisabledNotice>
                Additional watchlists are available only when AML Check Level is set to Enhanced.
              </DisabledNotice>
            )}

            <div>
              <label className="block text-sm text-[var(--text-secondary)] mb-2">
                {additionalWatchlistsTypesField?.formLabel || 'Watchlist types to include'}
              </label>
              <div className="text-xs text-[var(--text-muted)] mt-1 mb-3">
                <p>
                  {additionalWatchlistsTypesField?.formDescription ||
                    "Select the categories relevant to the customer's program."}
                </p>
                {amlConfig.vendor === 'refinitiv' && (
                  <span className="block mt-1">
                    <a
                      href="https://docs.google.com/spreadsheets/d/19im6wD9BnnNn27W6HY6RHLgOxB1J2qbf/edit?gid=2081824492#gid=2081824492"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      See Refinitiv watchlist reference →
                    </a>
                  </span>
                )}
                {amlConfig.vendor === 'comply' && (
                  <span className="block mt-1">
                    <a
                      href="https://docs.google.com/spreadsheets/d/14DKT1DkzzPjDsbuMwkyOivPtRC7n686y/edit?gid=1197965231#gid=1197965231"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      See ComplyAdvantage watchlist reference →
                    </a>
                  </span>
                )}
              </div>

              {/* ComplyAdvantage Watchlists Selector */}
              {amlConfig.vendor === 'comply' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                    Select Global Watchlists
                  </label>

                  {/* Search Input */}
                  <div className="mb-3">
                    <input
                      ref={watchlistInputRef}
                      type="text"
                      className="w-full h-10 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                      placeholder="Search watchlists..."
                      value={watchlistSearch}
                      onChange={(e) => {
                        setWatchlistSearch(e.target.value)
                        if (!showWatchlistDropdown) {
                          setShowWatchlistDropdown(true)
                        }
                      }}
                      onClick={() => {
                        if (!showWatchlistDropdown) {
                          setShowWatchlistDropdown(true)
                        }
                      }}
                      onFocus={() => {
                        if (!showWatchlistDropdown) {
                          setShowWatchlistDropdown(true)
                        }
                      }}
                      disabled={disabled || !enhancedEnabled}
                    />

                    {/* Dropdown Results */}
                    {showWatchlistDropdown && !disabled && enhancedEnabled && createPortal(
                      <div
                        ref={watchlistDropdownRef}
                        className="fixed z-[9999] bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-lg"
                        style={{
                          top: `${watchlistDropdownPos.top}px`,
                          left: `${watchlistDropdownPos.left}px`,
                          width: `${watchlistDropdownPos.width}px`
                        }}
                      >
                        <div className="max-h-64 overflow-y-auto">
                          {COMPLY_ADVANTAGE_WATCHLISTS
                            .filter(watchlist => {
                              const selected = amlConfig.additionalWatchlistsConfig.complyAdvanceWatchlistIds || []
                              const searchLower = watchlistSearch.toLowerCase()
                              const matchesSearch = !watchlistSearch ||
                                watchlist.name.toLowerCase().includes(searchLower) ||
                                watchlist.country.toLowerCase().includes(searchLower) ||
                                watchlist.type.toLowerCase().includes(searchLower)
                              return !selected.includes(watchlist.id) && matchesSearch
                            })
                            .slice(0, 50) // Limit to 50 results for performance
                            .map(watchlist => (
                              <button
                                key={watchlist.id}
                                type="button"
                                onClick={() => {
                                  const current = amlConfig.additionalWatchlistsConfig.complyAdvanceWatchlistIds || []
                                  updateAmlConfig('additionalWatchlistsConfig', {
                                    ...amlConfig.additionalWatchlistsConfig,
                                    complyAdvanceWatchlistIds: [...current, watchlist.id]
                                  })
                                  setWatchlistSearch('')
                                  setShowWatchlistDropdown(false)
                                }}
                                className="w-full px-4 py-3 text-left hover:bg-[var(--bg-tertiary)] transition-colors border-b border-[var(--border-color)] last:border-b-0"
                              >
                                <div className="flex items-center gap-2">
                                  <div className="flex-1">
                                    <div className="text-sm font-medium text-[var(--text-primary)]">
                                      {watchlist.name}
                                      <span className="ml-2 text-xs px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded">
                                        {watchlist.country}
                                      </span>
                                      <span className={`ml-1 text-xs px-1.5 py-0.5 rounded ${
                                        watchlist.type === 'warning' ? 'bg-yellow-500/20 text-yellow-400' :
                                        watchlist.type === 'fitness-probity' ? 'bg-purple-500/20 text-purple-400' :
                                        'bg-gray-500/20 text-gray-400'
                                      }`}>
                                        {watchlist.type}
                                      </span>
                                    </div>
                                    <div className="text-xs text-[var(--text-muted)]">
                                      {watchlist.entities.toLocaleString()} entities
                                    </div>
                                  </div>
                                </div>
                              </button>
                            ))}
                        </div>
                      </div>,
                      document.body
                    )}
                  </div>

                  {/* Selected Watchlists */}
                  {(amlConfig.additionalWatchlistsConfig.complyAdvanceWatchlistIds || []).length > 0 && (
                    <div className="space-y-1.5">
                      <div className="text-xs text-[var(--text-muted)] mb-2">
                        Selected: {(amlConfig.additionalWatchlistsConfig.complyAdvanceWatchlistIds || []).length} watchlists
                      </div>
                      {(amlConfig.additionalWatchlistsConfig.complyAdvanceWatchlistIds || []).map(id => {
                        const watchlist = COMPLY_ADVANTAGE_WATCHLISTS.find(w => w.id === id)
                        if (!watchlist) return null

                        return (
                          <div
                            key={id}
                            className="flex items-center gap-2 px-3 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg"
                          >
                            <div className="flex-1">
                              <div className="text-sm font-medium text-[var(--text-primary)]">
                                {watchlist.name}
                                <span className="ml-2 text-xs px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded">
                                  {watchlist.country}
                                </span>
                              </div>
                              <div className="text-xs text-[var(--text-muted)]">
                                {watchlist.entities.toLocaleString()} entities • {watchlist.type}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const current = amlConfig.additionalWatchlistsConfig.complyAdvanceWatchlistIds || []
                                updateAmlConfig('additionalWatchlistsConfig', {
                                  ...amlConfig.additionalWatchlistsConfig,
                                  complyAdvanceWatchlistIds: current.filter(i => i !== id)
                                })
                              }}
                              disabled={disabled}
                              className="ml-2 p-1 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors disabled:opacity-50"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}

              <CheckboxMultiSelect
                options={(
                  (additionalWatchlistsTypesField?.options || [
                    { value: 'law_enforcement', label: 'Law enforcement' },
                    { value: 'regulatory_enforcement', label: 'Regulatory enforcement' },
                    { value: 'disqualified_directors', label: 'Disqualified directors' },
                    { value: 'insolvency', label: 'Insolvency' },
                    { value: 'financial_crime', label: 'Financial crime' },
                    { value: 'country_specific', label: 'Country-specific' },
                    { value: 'all', label: 'All' },
                  ]) as Array<{ value: string; label: string }>
                ).map((o) => ({ id: o.value as any, label: o.label }))}
                value={amlConfig.additionalWatchlistsConfig.types}
                onChange={(next) =>
                  updateAmlConfig('additionalWatchlistsConfig', { ...amlConfig.additionalWatchlistsConfig, types: next })
                }
                disabled={disabled || !enhancedEnabled}
              />
            </div>
          </Section>

        </>
      )}
    </div>
  )
}
